import useAct from 'DesktopCaller/useAct'
import ServerWaitButton from 'Shared/components/ServerWaitButton'
import Modal from 'Shared/components/Modal/Modal'
import { formatDistanceToNow } from 'date-fns'
import './TargetList.scoped.scss'

export default function TargetList({ showDisconnect = false, heading = null }) {
  const act = useAct()
  const { availableTargets, loadingTargets } = useSel((s) => s.local)
  const [refreshing, setRefreshing] = useState(false)
  const [selectedTargetForHistory, setSelectedTargetForHistory] = useState(null)

  useEffect(() => {
    act.desktopCaller.fetchAvailableTargets()
  }, [])

  const handleRefresh = async () => {
    setRefreshing(true)
    await act.desktopCaller.fetchAvailableTargets()
    setRefreshing(false)
  }

  const handleSelectTarget = async (target) => {
    if (target.disabled) return
    await act.desktopCaller.selectTarget(target.id)
  }

  const formatLastCalled = (lastCalledAt) => {
    if (!lastCalledAt) return i18n.t('user.campaigns.phone.not_called_yet', { defaultValue: 'Not called yet' })
    return formatDistanceToNow(new Date(lastCalledAt), { addSuffix: true })
  }

  const defaultHeading = i18n.t('user.campaigns.phone.select_target', { defaultValue: 'Select someone to call' })
  const callAgainPrompt = i18n.t('user.campaigns.phone.call_again_prompt', { defaultValue: 'Do you want to call someone else?' })
  const displayHeading = heading ? `${heading}. ${callAgainPrompt}` : defaultHeading

  return (
    <div className="target-list box">
      <div className="target-list-header">
        <h2 className="no-margin-top no-margin-bottom">{displayHeading}</h2>
        <button
          className="button small secondary"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          {refreshing ? i18n.t('shared.messages.loading') : i18n.t('shared.messages.refresh', { defaultValue: 'Refresh' })}
        </button>
      </div>

      <p className="bold dark-grey">{i18n.t('user.campaigns.phone.people_you_can_call', { defaultValue: 'People you can call' })}</p>

      {(!availableTargets || availableTargets.length === 0) && (
        <p className="no-targets">{i18n.t('user.campaigns.phone.no_targets_available', { defaultValue: 'No targets available' })}</p>
      )}

      {availableTargets && availableTargets.length > 0 && (
        <div className="target-list-items">
          {availableTargets.map((target) => (
            <div
              key={target.id}
              className={`target-item ${target.disabled ? 'disabled' : ''}`}
              onClick={() => handleSelectTarget(target)}
            >
              <div className="target-info">
                <span className="target-name">{target.firstName} {target.lastName}</span>
                <div className="target-details">
                  <span className="target-external-id">{target.externalId}</span>
                  <div className="ellipsis"></div>
                  <span className="target-last-called">{formatLastCalled(target.lastCalledAt)}</span>
                </div>
              </div>
              <div className="target-stats">
                {target.callAttempts > 0 && (
                  <span className="call-attempts">
                    {i18n.t('user.campaigns.phone.call_attempts', { count: target.callAttempts, defaultValue: `${target.callAttempts} attempts` })}
                  </span>
                )}
                {target.successfulCallCount > 0 && (
                  <span className="successful-calls">
                    {i18n.t('user.campaigns.phone.successful_calls', { count: target.successfulCallCount, defaultValue: `${target.successfulCallCount} successful` })}
                  </span>
                )}
              </div>
              {target.disabled && (
                <div className="target-disabled-reason">
                  {target.disabledReason === 'in_progress' && (
                    <span className="badge in-progress">{i18n.t('user.campaigns.phone.in_progress', { defaultValue: 'In progress' })}</span>
                  )}
                  {target.disabledReason === 'max_calls_reached' && (
                    <span className="badge max-reached">{i18n.t('user.campaigns.phone.max_calls_reached', { defaultValue: 'Max calls reached' })}</span>
                  )}
                </div>
              )}
              {!target.disabled && (
                <div className="target-action">
                  {target.previousCalls && target.previousCalls.length > 0 && (
                    <button
                      className="button small secondary previous-calls-btn"
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedTargetForHistory(target)
                      }}
                    >
                      {i18n.t('user.campaigns.phone.previous_calls', { defaultValue: 'Previous calls' })}
                    </button>
                  )}
                  <span className="call-button">{i18n.t('user.campaigns.phone.call', { defaultValue: 'Call' })}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showDisconnect && (
        <div className="buttons margin-top">
          <ServerWaitButton
            className="button red small"
            onClick={() => act.desktopCaller.disconnect()}
          >{i18n.t('user.campaigns.phone.done_calling', { defaultValue: 'Done calling' })}</ServerWaitButton>
        </div>
      )}

      <Modal
        open={!!selectedTargetForHistory}
        title={selectedTargetForHistory ? `${i18n.t('user.campaigns.phone.previous_calls_for', { defaultValue: 'Previous calls for' })} ${selectedTargetForHistory.firstName} ${selectedTargetForHistory.lastName}` : ''}
        onCancel={() => setSelectedTargetForHistory(null)}
        defaultWider
      >
        {selectedTargetForHistory && (
          <PreviousCallsTable calls={selectedTargetForHistory.previousCalls} />
        )}
      </Modal>
    </div>
  )
}

function PreviousCallsTable({ calls }) {
  const formatCallTime = (calledAt) => {
    if (!calledAt) return ''
    const date = new Date(calledAt)
    return date.toLocaleString()
  }

  const hasAnswers = (call) => call.outcome === 'answer' && call.answers && call.answers.length > 0

  return (
    <table className="previous-calls-table">
      <thead>
        <tr>
          <th>{i18n.t('user.campaigns.phone.caller', { defaultValue: 'Caller' })}</th>
          <th>{i18n.t('user.campaigns.phone.time_called', { defaultValue: 'Time called' })}</th>
          <th>{i18n.t('user.campaigns.phone.notes', { defaultValue: 'Notes' })}</th>
        </tr>
      </thead>
      <tbody>
        {calls.map((call) => (
          <React.Fragment key={call.id}>
            <tr className={hasAnswers(call) ? 'has-answers' : ''}>
              <td>{call.callerName || '-'}</td>
              <td>{formatCallTime(call.calledAt)}</td>
              <td>{call.notes || '-'}</td>
            </tr>
            {hasAnswers(call) && (
              <tr className="answers-row">
                <td></td>
                <td colSpan={2}>
                  <div className="answers-list">
                    {call.answers.map((answer, idx) => (
                      <div key={idx} className="answer-item">
                        <strong>{answer.question}</strong>
                        <p>{answer.answer}</p>
                      </div>
                    ))}
                  </div>
                </td>
              </tr>
            )}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  )
}
